import { getAllStores } from '@api/store';
import { TInventory, TProduct, TStore } from '@components/types';
import { geoErrorMessage, getLocation, isValidLocation } from '@core/geolocation';
import { isStockQuantityExceeded, stockQuantityErrorMessage } from '@core/utils';
import { cartState } from '@recoil/atoms';
import { geoLocationState } from '@recoil/atoms/address';
import { variantState } from '@recoil/atoms/variant';
import { message, notification } from 'antd';
import { Minus, Plus, Trash } from "lucide-react";
import Link from 'next/link';
import { useState } from 'react';
import { useRecoilState } from 'recoil';

type Props = {
    product: TProduct;
    inventory: TInventory;
    mode: string;
    btnSize?: string;
    btnStyle?: string;
    textColor?: string;
    Isvariant?: boolean;
    variantId?: number;
    store?: TStore
}

export const CartButton = ({ product, inventory, mode, btnSize, btnStyle, textColor, Isvariant = true, variantId, store }: Props) => {
    const [cart, setCart] = useRecoilState(cartState);
    const [showModal, setShowModal] = useState(true);
    const [Variant, setVariant] = useRecoilState(variantState);
    const [geoPosition, setGeoposition] = useRecoilState(geoLocationState);
    const [api, contextHolder] = notification.useNotification();
    let tableInfo = typeof window !== 'undefined' ? JSON.parse(sessionStorage.getItem('tableInfo')) : null;
    const shouldApplyInventoryLimit = store?.storemeta?.settings?.inventory_limit === true;

    // Pure cart mutation — no geolocation concerns. Called once we know whether
    // (or don't need to know) the customer's location.
    const addOrUpdateCart = (product: TProduct, inventoryObj: TInventory, quantity: number = 1) => {
        let inventory: TInventory = JSON.parse(JSON.stringify(inventoryObj))
        const finalId = `${inventory?.variant?.id}${inventory?.addons.length > 0 ? "_" + inventory?.addons.sort((a, b) => a.id - b.id).map((x) => x.id).join("_") : ""}`
        let cartItem = cart.get(finalId)
        if (product?.variants?.length > 1 && typeof window !== 'undefined' && !(cartItem?.quantity > 0)) {
            setVariant({ Isvariant: true, product: product, showModal: showModal, quantity: productQuantity })
        }
        else if (inventory?.addons?.length > 0 && typeof window !== 'undefined' && !(cartItem?.quantity > 0)) {
            setVariant({ Isvariant: true, product: product, showModal: showModal, quantity: productQuantity, inventory: inventory })
        }
        else {
            (inventory?.variant_id !== undefined && inventory?.variant_id !== null) && setCart((cart) => {
                if (cart.has(finalId)) {
                    let newQuantity = cart.get(finalId).quantity + quantity;
                    if (shouldApplyInventoryLimit && quantity > 0 && isStockQuantityExceeded(cart, inventory, newQuantity, [finalId])) {
                        message?.error(stockQuantityErrorMessage(inventory));
                        return new Map(cart);
                    }
                    if (newQuantity == 0 || newQuantity == undefined) {
                        cart.delete(finalId)
                    }
                    else {
                        cart.set(finalId, { internalId: finalId, product: product, quantity: newQuantity, inventory: inventory, deliverable: store?.is_delivery || false })
                    }
                }
                else {
                    if (shouldApplyInventoryLimit && isStockQuantityExceeded(cart, inventory, quantity, [finalId])) {
                        message?.error(stockQuantityErrorMessage(inventory));
                        return new Map(cart);
                    }
                    cart.set(finalId, { internalId: finalId, product: product, quantity: quantity, inventory: inventory, deliverable: store?.is_delivery || false })
                }
                window?.fbq('track', 'AddToCart', {
                    content_name: `${product?.name || inventory?.product?.name}`,
                    content_category: `${product?.category?.name || inventory?.product?.category?.name}`,
                    content_ids: [`${finalId || product?.id || inventory?.id}`],
                    content_type: 'product',
                    value: inventory?.price || product?.mrp,
                    currency: 'RS'
                });
                return new Map(cart);
            }
            )
        }
    };

    const increase = (product: TProduct, inventoryObj: TInventory, quantity: number = 1) => {
        const isTableOrder = tableInfo?.table !== null && tableInfo?.table !== undefined;
        const hasKnownLocation = geoPosition?.latitude > 0 && geoPosition?.longitude > 0;

        // Dine-in orders don't need a delivery-distance check, and once we already
        // hold a location we don't need to re-prompt on every Add click.
        if (isTableOrder || hasKnownLocation) {
            addOrUpdateCart(product, inventoryObj, quantity);
            return;
        }

        // First Add click of the session with no location on file yet — this is
        // what actually triggers the browser's permission prompt. Add the item
        // either way once the prompt resolves; a denial shouldn't block the cart.
        getLocation()?.then((location) => {
            if (isValidLocation(location)) {
                setGeoposition({ last_updated: location?.last_updated || Date.now(), longitude: location?.longitude, latitude: location?.latitude, code: location?.code || 0 });
            } else {
                api.error({
                    message: geoErrorMessage(location?.code),
                    placement: 'bottomRight',
                });
            }
            addOrUpdateCart(product, inventoryObj, quantity);
        })
    };
    const productQuantity = inventory?.variant_id != undefined ? cart.get(inventory?.variant_id?.toString())?.quantity : 0;
    return (
        <>{contextHolder}{(productQuantity == 0 || productQuantity == undefined) ?
            (inventory?.external_link?.length > 0 ?
                <div className={`grid ${(productQuantity == 0 || productQuantity == undefined) ? "grid-cols-1" : "grid-cols-3 divide-x rounded shadow"} justify-start items-center max-w-lg min-w-full text-neutral-50`}>
                    <Link
                        onClick={() => {
                            window?.fbq('trackCustom', 'BuyNowClicked', {
                                link: inventory?.external_link,
                                content_name: `${product?.name || inventory?.product?.name}`,
                                content_category: `${product?.category?.name || inventory?.product?.category?.name}`,
                                content_ids: [`${product?.id || inventory?.id}`],
                                content_type: 'product',
                                value: inventory?.price || product?.mrp,
                                currency: 'RS'
                            });
                        }}
                        href={inventory?.external_link} className={`font-manrope py-2 md:text-base text-sm text-center font-semibold whitespace-nowrap select-none rounded flex justify-center items-center ${btnStyle?.length > 0 ? btnStyle : " h-8 px-8 bg-brand_indigo"}`}>BUY NOW</Link>
                </div>
                :
                <div className={`flex flex-row${(productQuantity == 0 || productQuantity == undefined) ? " " : " divide-x rounded shadow"} justify-start items-center max-w-lg min-w-full bg-business-100 border-1 border-solid border-business rounded-md md:text-sm py-1`}>
                    <button onClick={() => increase(product, inventory)} className={`font-manrope md:text-base text-sm text-center font-semibold whitespace-nowrap select-none rounded w-full text-business cursor-pointer`}>ADD</button>
                </div>)
            :
            <div className="grid grid-cols-3 w-full bg-business items-center rounded-md py-1">
                <div className="col-span-1 flex justify-start items-center py-0.5">
                    <button onClick={(event) => increase(product, inventory, -1)} className={`flex cursor-pointer items-center md:text-base text-sm md:px-3 px-2 rounded-md text-neutral-50 font-semibold`}>
                        {productQuantity == 1 ? <Trash size={14} /> : < Minus size={14} />}
                    </button>
                </div>
                <div className="col-span-1 flex justify-center items-center px-2 text-center py-0.5"><span className={`font-manrope md:text-base text-sm text-neutral-50 font-semibold select-none`}>{productQuantity}</span></div>
                <div className="col-span-1 flex justify-end items-center rounded py-0.5">
                    <button onClick={(event) => increase(product, inventory)} className={`flex cursor-pointer items-center md:text-base text-sm md:px-3 px-2 rounded-md text-neutral-50 font-semibold`} ><Plus size={14} /></button>
                </div>
            </div>
        }</>
    )
}
